<template>
    <div class="recharge-audit-container">
      <el-card shadow="never">
        <template #header>
          <div class="card-header">
            <span class="title">充值订单审核</span>
          </div>
        </template>
  
        <!-- 搜索筛选区域 -->
        <el-form :model="searchForm" inline class="search-form">
          <el-form-item label="订单号">
            <el-input v-model="searchForm.orderNo" placeholder="请输入订单号" clearable />
          </el-form-item>
          <el-form-item label="支付方式">
            <el-select v-model="searchForm.paytype" placeholder="全部" clearable>
              <el-option label="微信" value="WECHAT_PAY" />
              <el-option label="对公转账" value="CORPORATE_TRANSFER" />
            </el-select>
          </el-form-item>
          <el-form-item label="订单状态">
            <el-select v-model="searchForm.status" placeholder="全部" clearable>
              <el-option label="待审核" value="PENDING" />
              <el-option label="支付成功" value="SUCCESS" />
              <el-option label="超时关闭" value="TIMEOUT" />
            </el-select>
          </el-form-item>
          <el-form-item label="创建时间">
            <el-date-picker
              v-model="searchForm.dateRange"
              type="datetimerange"
              range-separator="至"
              start-placeholder="开始时间"
              end-placeholder="结束时间"
              value-format="YYYY-MM-DD HH:mm:ss"
            />
          </el-form-item>
          <el-form-item>
            <el-button type="primary" @click="handleQuery">查询</el-button>
            <el-button @click="handleReset">重置</el-button>
            <el-button type="success" @click="handleExport">导出列表</el-button>
          </el-form-item>
        </el-form>
  
        <!-- 订单表格 -->
        <el-table
          :data="tableData"
          border
          stripe
          style="width:100%"
          v-loading="loading"
        >
          <el-table-column prop="orderNo" label="充值订单号" min-width="220" />
          <el-table-column prop="sub_id" label="子商户ID" width="120" />
          <el-table-column prop="paytype" label="支付方式" width="140">
            <template #default="{ row }">
              <el-tag v-if="row.paytype === 'WECHAT_PAY'" type="success">微信</el-tag>
              <el-tag v-else-if="row.paytype === 'CORPORATE_TRANSFER'" type="warning">对公转账</el-tag>
              <el-tag v-else type="info">{{ row.paytype }}</el-tag>
            </template>
          </el-table-column>
          <el-table-column prop="amount" label="充值金额(元)" width="140" align="right">
            <template #default="{ row }">
              <span class="money-text">{{ row.amount}}</span>
            </template>
          </el-table-column>
          <el-table-column label="赠送金额(元)" width="140" align="right">
            <template #default>-</template>
          </el-table-column>
          <el-table-column prop="status" label="订单状态" width="140">
            <template #default="{ row }">
              <el-tag v-if="row.status === 'PENDING'" type="warning">待审核</el-tag>
              <el-tag v-else-if="row.status === 'SUCCESS'" type="success">支付成功</el-tag>
              <el-tag v-else-if="row.status === 'TIMEOUT'" type="info">超时关闭</el-tag>
              <el-tag v-else type="info">{{ row.status }}</el-tag>
            </template>
          </el-table-column>
          <el-table-column label="转账凭证" width="140">
            <template #default="{ row }">
              <el-button
                v-if="row.paytype === 'CORPORATE_TRANSFER' && row.transferVoucher"
                link
                type="primary"
                @click="previewImage(row.transferVoucher)"
              >
                查看凭证
              </el-button>
              <span v-else>—</span>
            </template>
          </el-table-column>
          <el-table-column prop="auditRemark" label="审核备注" min-width="160">
            <template #default="{ row }">
              {{ row.auditRemark ?? '—' }}
            </template>
          </el-table-column>
          <el-table-column prop="create_time" label="下单时间" min-width="180">
            <template #default="{ row }">
              {{ formatTime(row.create_time) }}
            </template>
          </el-table-column>
          <el-table-column prop="pay_time" label="支付时间" min-width="180">
            <template #default="{ row }">
              {{ formatTime(row.pay_time) }}
            </template>
          </el-table-column>
          <el-table-column label="操作" width="240" fixed="right">
            <template #default="{ row }">
              <el-button link type="primary" @click="openDetail(row)">详情</el-button>
              <!-- 条件：对公转账 + PENDING待审核，显示审核按钮 -->
              <template v-if="row.paytype === 'CORPORATE_TRANSFER' && row.status === 'PENDING'">
                <el-button link type="success" @click="openAuditModal(row)">审核</el-button>
              </template>
              <span v-else class="text-gray">不可审核</span>
            </template>
          </el-table-column>
        </el-table>
  
        <!-- 分页 -->
        <el-pagination
          v-model:current-page="page.page"
          v-model:page-size="page.pageSize"
          :total="page.total"
          :page-sizes="[10,20,50,100]"
          layout="total, sizes, prev, pager, next, jumper"
          @size-change="handleQuery"
          @current-change="handleQuery"
          class="pagination-wrap"
        />
      </el-card>
  
      <!-- 对公订单审核弹窗 -->
      <el-dialog v-model="auditDialog.visible" title="对公充值订单审核" width="620px" :close-on-click-modal="false">
        <el-descriptions :column="2" border>
          <el-descriptions-item label="订单号">{{ auditDialog.row.orderNo }}</el-descriptions-item>
          <el-descriptions-item label="子商户ID">{{ auditDialog.row.sub_id }}</el-descriptions-item>
          <el-descriptions-item label="充值金额">
            {{ (auditDialog.row.amount / 100).toFixed(2) }} 元
          </el-descriptions-item>
          <el-descriptions-item label="转账凭证">
            <el-button
              v-if="auditDialog.row.transferVoucher"
              link
              @click="previewImage(auditDialog.row.transferVoucher)"
            >查看凭证</el-button>
            <span v-else>—</span>
          </el-descriptions-item>
        </el-descriptions>
  
        <el-form :model="auditForm" label-width="100px" class="audit-form">
          <el-form-item label="审核结果">
            <el-radio-group v-model="auditForm.auditResult">
              <el-radio :label="1">审核通过</el-radio>
              <el-radio :label="2">驳回</el-radio>
            </el-radio-group>
          </el-form-item>
          <el-form-item label="审核备注">
            <el-input
              v-model="auditForm.auditRemark"
              type="textarea"
              :rows="4"
              placeholder="请填写审核备注，驳回必须填写原因"
            />
          </el-form-item>
        </el-form>
  
        <template #footer>
          <el-button @click="auditDialog.visible = false">取消</el-button>
          <el-button type="primary" @click="submitAudit" :loading="auditSubmitting">提交审核</el-button>
        </template>
      </el-dialog>
  
      <!-- 图片预览 -->
      <el-image-viewer v-if="previewViewer.show" :url-list="[previewViewer.src]" @close="previewViewer.show = false" />
    </div>
  </template>
  
  <script setup>
  import { ref, reactive, onMounted } from 'vue'
  import { ElMessage, ElMessageBox } from 'element-plus'
  import { getRechargeList, dgchongzhi } from '@/api/api'
  
  const loading = ref(false)
  // 审核提交loading，防止重复点击
  const auditSubmitting = ref(false)
  
  //搜索表单 对齐后端入参字段
  const searchForm = reactive({
    orderNo: '',
    paytype: '',
    status: '',
    dateRange: []
  })
  
  //分页 对齐后端返回字段 page pageSize total
  const page = reactive({
    page: 1,
    pageSize: 10,
    total: 0
  })
  
  const tableData = ref([])
  
  //审核弹窗
  const auditDialog = reactive({
    visible: false,
    row: {}
  })
  const auditForm = reactive({
    auditResult: 1,
    auditRemark: ''
  })
  
  //图片预览
  const previewViewer = reactive({
    show: false,
    src: ''
  })
  
  //时间格式化，后端返回 ISO时间 2026-08-10T10:34:54
  function formatTime(str) {
    if (!str) return '—'
    return str.replace('T', ' ')
  }
  
  //查询列表 正式接口
  async function handleQuery() {
    loading.value = true
    try {
      const params = {
        page: page.page,
        pageSize: page.pageSize,
        orderNo: searchForm.orderNo || undefined,
        paytype: searchForm.paytype || undefined,
        status: searchForm.status || undefined
      }
      const res = await getRechargeList(params)
      if (res.code === 200 && res.subcode === 200001) {
        tableData.value = res.data.list || []
        page.total = res.data.total || 0
        page.pageSize = res.data.pageSize
      } else {
        ElMessage.warning(res.message || '获取订单失败')
      }
    } catch (err) {
      ElMessage.error('网络异常，获取列表失败')
    } finally {
      loading.value = false
    }
  }
  
  //重置搜索
  function handleReset() {
    searchForm.orderNo = ''
    searchForm.paytype = ''
    searchForm.status = ''
    searchForm.dateRange = []
    page.page = 1
    handleQuery()
  }
  
  //导出
  async function handleExport() {
    ElMessage.info('导出待对接后端导出接口')
  }
  
  //打开审核弹窗
  function openAuditModal(row) {
    auditDialog.row = { ...row }
    auditDialog.visible = true
    auditForm.auditResult = 1
    auditForm.auditRemark = ''
    auditSubmitting.value = false
  }
  
  //提交对公审核，增加二次确认弹窗
  async function submitAudit() {
    if (auditForm.auditResult === 2 && !auditForm.auditRemark?.trim()) {
      return ElMessage.warning('驳回请填写驳回原因')
    }
    if(auditSubmitting.value) return
  
    //二次确认弹窗
    let confirmText = auditForm.auditResult === 1
      ? `确认【审核通过】订单：${auditDialog.row.orderNo}，执行对公充值到账？`
      : `确认【驳回】订单：${auditDialog.row.orderNo}？\n备注：${auditForm.auditRemark}`
  
    try {
      await ElMessageBox.confirm(confirmText, '操作确认', {
        confirmButtonText: '确认提交',
        cancelButtonText: '取消',
        type: 'warning'
      })
    } catch {
      // 用户点取消，直接返回
      return
    }
  
    auditSubmitting.value = true
    try {
      if (auditForm.auditResult === 1) {
        //审核通过，调用 dgchongzhi，只传orderNo
        const res = await dgchongzhi({ orderNo: auditDialog.row.orderNo })
        if (res.code === 200) {
          ElMessage.success('审核通过，操作完成')
        } else {
          ElMessage.warning(res.message || '审批接口返回异常')
        }
      } else {
        //驳回：当前没有驳回接口，提示，后续对接驳回接口在这里写逻辑
        ElMessage.info('驳回：当前后端暂未提供驳回接口，请后端完成驳回接口后补充代码；备注已录入前端')
      }
  
      auditDialog.visible = false
      handleQuery()
    } catch (e) {
      ElMessage.error('审核提交失败')
    } finally {
      auditSubmitting.value = false
    }
  }
  
  //图片预览
  function previewImage(url) {
    previewViewer.src = url
    previewViewer.show = true
  }
  
  //订单详情
  function openDetail(row) {
    ElMessage.info(`查看订单 ${row.orderNo}`)
  }
  
  onMounted(() => {
    handleQuery()
  })
  </script>
  
  <style scoped>
  .recharge-audit-container {
    padding: 16px;
  }
  .card-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }
  .title {
    font-size: 16px;
    font-weight: 600;
  }
  .search-form {
    margin-bottom:16px;
  }
  .pagination-wrap {
    margin-top:16px;
    text-align:right;
  }
  .money-text {
    color:#f56c6c;
    font-weight:500;
  }
  .text-gray {
    color:#909399;
  }
  .audit-form {
    margin-top:12px;
  }
  </style>